export const ASK_HELIX_DISPLAY_VALUE_PACKET_SCHEMA_VERSION =
  'ask_helix_display_value_packet_v1' as const;

export type AskHelixDisplayValueStateV1 =
  | Readonly<{
      kind: 'current';
      bindingRef: string;
      asOf: string | null;
    }>
  | Readonly<{
      kind: 'dated_prior';
      bindingRef: string;
      asOf: string | null;
    }>
  | Readonly<{
      kind: 'no_value';
      bindingRef: null;
      asOf: null;
      reason: 'not_yet_quantified' | 'not_applicable' | 'restricted';
    }>;

export type AskHelixDisplayValueEligibilityV1 = Readonly<{
  standaloneVisible: boolean;
  currentOpportunitySetEligible: boolean;
  additiveCurrentTotalEligible: boolean;
  signedOffPackageEligible: boolean;
}>;

export type AskHelixExecutiveValueConditionV1 =
  | 'signed_off'
  | 'current_estimate'
  | 'awaiting_finance_signoff'
  | 'figures_may_overlap'
  | 'needs_cleaner_data'
  | 'refresh_needed'
  | 'not_yet_quantified'
  | 'not_applicable'
  | 'restricted';

export type AskHelixValueRemediationV1 = Readonly<{
  kind:
    | 'cleaner_data'
    | 'refresh_calculation'
    | 'connect_calculation'
    | 'resolve_overlap'
    | 'finance_signoff'
    | 'quantify_lever'
    | 'request_access';
  owner: 'finance' | 'data' | 'engineering' | 'operations' | 'security';
  actionLabel: string;
}>;

export type AskHelixDisplayValuePacketV1 = Readonly<{
  schemaVersion: typeof ASK_HELIX_DISPLAY_VALUE_PACKET_SCHEMA_VERSION;
  state: AskHelixDisplayValueStateV1;
  eligibility: AskHelixDisplayValueEligibilityV1;
  condition: AskHelixExecutiveValueConditionV1;
  remediation: AskHelixValueRemediationV1 | null;
}>;

/**
 * Resolves one binding at the consuming boundary. The shared packet deliberately carries
 * no duplicate numeric amount and has no dependency on a binding registry implementation.
 */
export type AskHelixDisplayValueBindingResolverV1 = (
  bindingRef: string,
) => unknown;

export type AskHelixDisplayValuePacketValidationResultV1 =
  | Readonly<{
      ok: true;
      packet: AskHelixDisplayValuePacketV1;
      diagnostics?: readonly string[];
    }>
  | Readonly<{ ok: false; errors: readonly string[] }>;

const PACKET_KEYS = [
  'schemaVersion',
  'state',
  'eligibility',
  'condition',
  'remediation',
] as const;
const VALUE_STATE_KEYS = ['kind', 'bindingRef', 'asOf'] as const;
const NO_VALUE_STATE_KEYS = ['kind', 'bindingRef', 'asOf', 'reason'] as const;
const ELIGIBILITY_KEYS = [
  'standaloneVisible',
  'currentOpportunitySetEligible',
  'additiveCurrentTotalEligible',
  'signedOffPackageEligible',
] as const;
const REMEDIATION_KEYS = ['kind', 'owner', 'actionLabel'] as const;
const DUPLICATE_NUMERIC_AUTHORITY_KEYS = new Set([
  'amount',
  'amountusd',
  'value',
  'raw',
  'rawvalue',
  'displayvalue',
  'numericvalue',
]);
const PRESENTATION_METADATA_NODE_LIMIT = 2_048;
const PRESENTATION_METADATA_DEPTH_LIMIT = 64;
const NUMERIC_AUTHORITY_STRING_PATTERN =
  /^\s*(?:(?:USD|EUR|GBP|JPY|CAD|AUD)\s+|[$€£¥]\s*)?\(?[+-]?(?:(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?|\.\d+)\)?\s*(?:%|[KMBT]|USD|EUR|GBP|JPY|CAD|AUD)?\s*$/i;

const EXECUTIVE_VALUE_CONDITIONS: readonly AskHelixExecutiveValueConditionV1[] = [
  'signed_off',
  'current_estimate',
  'awaiting_finance_signoff',
  'figures_may_overlap',
  'needs_cleaner_data',
  'refresh_needed',
  'not_yet_quantified',
  'not_applicable',
  'restricted',
];
const NO_VALUE_REASONS: readonly Extract<
  AskHelixDisplayValueStateV1,
  { kind: 'no_value' }
>['reason'][] = ['not_yet_quantified', 'not_applicable', 'restricted'];
const REMEDIATION_KINDS: readonly AskHelixValueRemediationV1['kind'][] = [
  'cleaner_data',
  'refresh_calculation',
  'connect_calculation',
  'resolve_overlap',
  'finance_signoff',
  'quantify_lever',
  'request_access',
];
const REMEDIATION_OWNERS: readonly AskHelixValueRemediationV1['owner'][] = [
  'finance',
  'data',
  'engineering',
  'operations',
  'security',
];

const PUBLIC_ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const PUBLIC_UTC_TIMESTAMP_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,9})?Z$/;

export function validateAskHelixDisplayValuePacketV1(
  candidate: unknown,
  resolveBinding: AskHelixDisplayValueBindingResolverV1,
): AskHelixDisplayValuePacketValidationResultV1 {
  const errors: string[] = [];
  if (!isRecord(candidate)) {
    return { ok: false, errors: ['packet must be an object'] };
  }

  rejectUnexpectedKeys(candidate, PACKET_KEYS, 'packet', errors);
  if (candidate.schemaVersion !== ASK_HELIX_DISPLAY_VALUE_PACKET_SCHEMA_VERSION) {
    errors.push(
      `schemaVersion must be ${ASK_HELIX_DISPLAY_VALUE_PACKET_SCHEMA_VERSION}`,
    );
  }
  for (const key of PACKET_KEYS) {
    rejectDuplicateNumericAuthorityFields(candidate[key], key, errors);
  }

  if (isDisplayableValueStateCandidate(candidate.state)) {
    const bindingRef = validateBinding(
      candidate.state.bindingRef,
      resolveBinding,
      errors,
    );
    if (errors.length > 0 || bindingRef === null) {
      return { ok: false, errors };
    }
    const presentation = canonicalizeDisplayablePresentation(
      candidate,
      candidate.state,
      bindingRef,
    );
    const crossField = canonicalizeCrossFieldMetadata(presentation.packet);
    const diagnostics = uniqueDiagnostics([
      ...presentation.diagnostics,
      ...crossField.diagnostics,
    ]);
    if (diagnostics.length === 0) {
      return { ok: true, packet: crossField.packet };
    }
    return {
      ok: true,
      packet: crossField.packet,
      diagnostics,
    };
  }

  validateNonDisplayableState(candidate.state, errors);
  validateEligibility(candidate.eligibility, errors);

  if (!isOneOf(candidate.condition, EXECUTIVE_VALUE_CONDITIONS)) {
    errors.push('condition must be one of the closed executive value conditions');
  }
  validateRemediation(candidate.remediation, errors);

  if (errors.length > 0) {
    return { ok: false, errors };
  }
  const canonical = canonicalizeCrossFieldMetadata(candidate as AskHelixDisplayValuePacketV1);
  if (canonical.diagnostics.length === 0) {
    return { ok: true, packet: canonical.packet };
  }
  return {
    ok: true,
    packet: canonical.packet,
    diagnostics: canonical.diagnostics,
  };
}

function validateNonDisplayableState(
  value: unknown,
  errors: string[],
): void {
  if (!isRecord(value)) {
    errors.push('state must be an object');
    return;
  }

  if (value.kind === 'no_value') {
    rejectUnexpectedKeys(value, NO_VALUE_STATE_KEYS, 'state', errors);
    if (value.bindingRef !== null || value.asOf !== null) {
      errors.push('no_value state must not carry a binding or as-of timestamp');
    }
    if (!isOneOf(value.reason, NO_VALUE_REASONS)) {
      errors.push('no_value reason must be one of the three allowed reasons');
    }
    return;
  }

  errors.push('state.kind must be current, dated_prior, or no_value');
}

function validateBinding(
  value: unknown,
  resolveBinding: AskHelixDisplayValueBindingResolverV1,
  errors: string[],
): string | null {
  if (!isNonEmptyString(value)) {
    errors.push('state.bindingRef must be a non-empty string');
    return null;
  }
  let resolvedValue: unknown;
  try {
    resolvedValue = resolveBinding(value);
  } catch {
    resolvedValue = undefined;
  }
  if (typeof resolvedValue !== 'number' || !Number.isFinite(resolvedValue)) {
    errors.push('state.bindingRef must resolve to one finite numeric binding');
    return null;
  }
  return value;
}

type DisplayableValueStateCandidate = Readonly<Record<string, unknown>> &
  Readonly<{ kind: 'current' | 'dated_prior' }>;

type CanonicalizedDisplayablePresentation = Readonly<{
  packet: AskHelixDisplayValuePacketV1;
  diagnostics: readonly string[];
}>;

const CONSERVATIVE_DISPLAYABLE_ELIGIBILITY: AskHelixDisplayValueEligibilityV1 = {
  standaloneVisible: true,
  currentOpportunitySetEligible: false,
  additiveCurrentTotalEligible: false,
  signedOffPackageEligible: false,
};

/**
 * Once one recognized binding resolves to a finite number, presentation metadata may
 * qualify or exclude that value from aggregates but cannot erase the standalone value.
 */
function canonicalizeDisplayablePresentation(
  candidate: Readonly<Record<string, unknown>>,
  stateCandidate: DisplayableValueStateCandidate,
  bindingRef: string,
): CanonicalizedDisplayablePresentation {
  const diagnostics: string[] = [];
  const validAsOf =
    isNonEmptyString(stateCandidate.asOf) &&
    isValidPublicIsoDateOrUtcTimestamp(stateCandidate.asOf)
      ? stateCandidate.asOf
      : null;
  const dateIsValid =
    stateCandidate.kind === 'current'
      ? stateCandidate.asOf === null || validAsOf !== null
      : validAsOf !== null;
  if (!dateIsValid) {
    diagnostics.push(
      stateCandidate.kind === 'current'
        ? 'Current value retained; value date was unavailable.'
        : 'Dated prior value retained; comparison date was unavailable.',
    );
  }

  const stateHasUnexpectedFields = hasUnexpectedKeys(
    stateCandidate,
    VALUE_STATE_KEYS,
  );
  if (stateHasUnexpectedFields) {
    diagnostics.push(
      'Value retained; unsupported presentation metadata was ignored.',
    );
  }
  const state: Extract<
    AskHelixDisplayValueStateV1,
    { kind: 'current' | 'dated_prior' }
  > =
    dateIsValid && !stateHasUnexpectedFields
      ? (stateCandidate as Extract<
          AskHelixDisplayValueStateV1,
          { kind: 'current' | 'dated_prior' }
        >)
      : {
          kind: stateCandidate.kind,
          bindingRef,
          asOf: validAsOf,
        };

  const validEligibility = isValidEligibility(candidate.eligibility)
    ? candidate.eligibility
    : null;
  const validCondition = isOneOf(
    candidate.condition,
    EXECUTIVE_VALUE_CONDITIONS,
  )
    ? candidate.condition
    : null;
  const conditionIsValid = validCondition !== null;
  const validRemediation = isValidRemediation(candidate.remediation)
    ? candidate.remediation
    : undefined;
  const remediationIsValid = validRemediation !== undefined;
  const otherPresentationIsDefective =
    !dateIsValid ||
    stateHasUnexpectedFields ||
    !conditionIsValid ||
    !remediationIsValid;
  let eligibility: AskHelixDisplayValueEligibilityV1;
  let eligibilityRequiresCorrection: boolean;
  if (
    validEligibility === null ||
    (otherPresentationIsDefective &&
      !eligibilityMatches(
        validEligibility,
        CONSERVATIVE_DISPLAYABLE_ELIGIBILITY,
      ))
  ) {
    eligibility = CONSERVATIVE_DISPLAYABLE_ELIGIBILITY;
    eligibilityRequiresCorrection = true;
  } else {
    eligibility = validEligibility;
    eligibilityRequiresCorrection = false;
  }
  if (eligibilityRequiresCorrection) {
    diagnostics.push(
      'Value retained; eligibility was limited to standalone display.',
    );
  }

  const condition = validCondition ?? 'needs_cleaner_data';
  if (!conditionIsValid) {
    diagnostics.push(
      'Value retained; business condition was set to needs cleaner data.',
    );
  }

  const remediation = validRemediation ?? null;
  if (!remediationIsValid) {
    diagnostics.push(
      'Value retained; invalid remediation was removed without assigning an owner or action.',
    );
  }

  const presentationChanged =
    state !== stateCandidate ||
    eligibility !== candidate.eligibility ||
    condition !== candidate.condition ||
    remediation !== candidate.remediation;
  if (!presentationChanged) {
    return {
      packet: candidate as AskHelixDisplayValuePacketV1,
      diagnostics,
    };
  }
  return {
    packet: {
      schemaVersion: ASK_HELIX_DISPLAY_VALUE_PACKET_SCHEMA_VERSION,
      state,
      eligibility,
      condition,
      remediation,
    },
    diagnostics,
  };
}

function isDisplayableValueStateCandidate(
  value: unknown,
): value is DisplayableValueStateCandidate {
  return (
    isRecord(value) &&
    (value.kind === 'current' || value.kind === 'dated_prior')
  );
}

function isValidEligibility(
  value: unknown,
): value is AskHelixDisplayValueEligibilityV1 {
  return (
    isRecord(value) &&
    !hasUnexpectedKeys(value, ELIGIBILITY_KEYS) &&
    ELIGIBILITY_KEYS.every((key) => typeof value[key] === 'boolean')
  );
}

function isValidRemediation(
  value: unknown,
): value is AskHelixValueRemediationV1 | null {
  if (value === null) return true;
  return (
    isRecord(value) &&
    !hasUnexpectedKeys(value, REMEDIATION_KEYS) &&
    isOneOf(value.kind, REMEDIATION_KINDS) &&
    isOneOf(value.owner, REMEDIATION_OWNERS) &&
    isNonEmptyString(value.actionLabel)
  );
}

function uniqueDiagnostics(diagnostics: readonly string[]): readonly string[] {
  return [...new Set(diagnostics)];
}

function isValidPublicIsoDateOrUtcTimestamp(value: string): boolean {
  const dateOnly = value.match(PUBLIC_ISO_DATE_PATTERN);
  if (dateOnly) {
    return isValidCalendarDate(dateOnly[1], dateOnly[2], dateOnly[3]);
  }

  const timestamp = value.match(PUBLIC_UTC_TIMESTAMP_PATTERN);
  if (!timestamp) return false;
  if (!isValidCalendarDate(timestamp[1], timestamp[2], timestamp[3])) {
    return false;
  }
  const hour = Number(timestamp[4]);
  const minute = Number(timestamp[5]);
  const second = Number(timestamp[6]);
  return hour <= 23 && minute <= 59 && second <= 59;
}

function isValidCalendarDate(
  yearText: string,
  monthText: string,
  dayText: string,
): boolean {
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  if (year < 1 || month < 1 || month > 12 || day < 1) return false;
  const daysInMonth = [
    31,
    isLeapYear(year) ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];
  return day <= daysInMonth[month - 1];
}

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function validateEligibility(value: unknown, errors: string[]): void {
  if (!isRecord(value)) {
    errors.push('eligibility must be an object');
    return;
  }

  rejectUnexpectedKeys(value, ELIGIBILITY_KEYS, 'eligibility', errors);
  for (const key of ELIGIBILITY_KEYS) {
    if (typeof value[key] !== 'boolean') {
      errors.push(`eligibility.${key} must be a boolean`);
    }
  }
}

function validateRemediation(value: unknown, errors: string[]): void {
  if (value === null) {
    return;
  }
  if (!isRecord(value)) {
    errors.push('remediation must be an object or null');
    return;
  }

  rejectUnexpectedKeys(value, REMEDIATION_KEYS, 'remediation', errors);
  if (!isOneOf(value.kind, REMEDIATION_KINDS)) {
    errors.push('remediation.kind must be one of the closed remediation kinds');
  }
  if (!isOneOf(value.owner, REMEDIATION_OWNERS)) {
    errors.push('remediation.owner must be one of the closed remediation owners');
  }
  if (!isNonEmptyString(value.actionLabel)) {
    errors.push('remediation.actionLabel must be a non-empty string');
  }
}

type CanonicalizedDisplayValuePacket = Readonly<{
  packet: AskHelixDisplayValuePacketV1;
  diagnostics: readonly string[];
}>;
const NO_VALUE_ONLY_CONDITIONS: readonly AskHelixExecutiveValueConditionV1[] = [
  'not_yet_quantified',
  'not_applicable',
  'restricted',
];

/**
 * Binding/state failures remain hard errors. Closed-but-contradictory display metadata is
 * made conservative only after the binding has resolved, so a real value is never erased.
 */
function canonicalizeCrossFieldMetadata(
  packet: AskHelixDisplayValuePacketV1,
): CanonicalizedDisplayValuePacket {
  if (packet.state.kind === 'dated_prior') {
    return canonicalizeDatedPrior(packet);
  }
  if (packet.state.kind === 'no_value') {
    return canonicalizeNoValue(packet, packet.state.reason);
  }
  return canonicalizeCurrent(packet);
}

function canonicalizeCurrent(
  packet: AskHelixDisplayValuePacketV1,
): CanonicalizedDisplayValuePacket {
  const eligibility = { ...packet.eligibility };
  let condition = packet.condition;
  let remediation = packet.remediation;
  let diagnostic: string | null = null;

  if (!eligibility.standaloneVisible) {
    eligibility.standaloneVisible = true;
    diagnostic = 'Current value retained; eligibility was conservatively corrected.';
  }
  if (!eligibility.currentOpportunitySetEligible && eligibility.additiveCurrentTotalEligible) {
    eligibility.additiveCurrentTotalEligible = false;
    diagnostic ??= 'Current value retained; eligibility was conservatively corrected.';
  }

  if (NO_VALUE_ONLY_CONDITIONS.includes(condition)) {
    const fallback = conservativeCurrentFallback(remediation);
    condition = fallback.condition;
    remediation = fallback.remediation;
    if (condition === 'figures_may_overlap') {
      eligibility.additiveCurrentTotalEligible = false;
    }
    eligibility.signedOffPackageEligible = false;
    diagnostic =
      'Current value retained; source and status metadata require confirmation.';
  } else if (condition === 'figures_may_overlap') {
    if (eligibility.additiveCurrentTotalEligible || eligibility.signedOffPackageEligible) {
      eligibility.additiveCurrentTotalEligible = false;
      eligibility.signedOffPackageEligible = false;
      diagnostic = 'Current value retained; Finance must resolve overlap before totals.';
    }
  } else if (condition === 'signed_off') {
    const signedOffIsCoherent =
      packet.state.kind === 'current' &&
      packet.state.asOf !== null &&
      eligibility.currentOpportunitySetEligible &&
      eligibility.additiveCurrentTotalEligible &&
      eligibility.signedOffPackageEligible &&
      remediation === null;
    if (!signedOffIsCoherent) {
      eligibility.signedOffPackageEligible = false;
      const fallback = conservativeCurrentFallback(remediation);
      condition = fallback.condition;
      remediation = fallback.remediation;
      if (condition === 'figures_may_overlap') {
        eligibility.additiveCurrentTotalEligible = false;
        diagnostic = 'Current value retained; Finance must resolve overlap before totals.';
      } else {
        diagnostic =
          'Current value retained; source and status metadata require confirmation.';
      }
    }
  } else if (eligibility.signedOffPackageEligible) {
    eligibility.signedOffPackageEligible = false;
    diagnostic ??= 'Current value retained; eligibility was conservatively corrected.';
  }

  if (packet.state.kind === 'current' && packet.state.asOf === null) {
    const aggregateEligibilityChanged =
      eligibility.additiveCurrentTotalEligible ||
      eligibility.signedOffPackageEligible;
    eligibility.additiveCurrentTotalEligible = false;
    eligibility.signedOffPackageEligible = false;
    if (aggregateEligibilityChanged) {
      diagnostic ??=
        'Current value retained; a missing source date excludes it from additive and signed-off totals.';
    }
  }

  return finishCanonicalization(packet, eligibility, condition, remediation, diagnostic);
}

function canonicalizeDatedPrior(
  packet: AskHelixDisplayValuePacketV1,
): CanonicalizedDisplayValuePacket {
  const eligibility: AskHelixDisplayValueEligibilityV1 = {
    standaloneVisible: true,
    currentOpportunitySetEligible: false,
    additiveCurrentTotalEligible: false,
    signedOffPackageEligible: false,
  };
  let condition = packet.condition;
  let remediation = packet.remediation;
  let diagnostic = eligibilityMatches(packet.eligibility, eligibility)
    ? null
    : 'Dated prior value retained; use it only as a historical comparison.';

  if (NO_VALUE_ONLY_CONDITIONS.includes(condition)) {
    if (
      remediation?.kind === 'refresh_calculation' &&
      remediation.owner === 'engineering'
    ) {
      condition = 'refresh_needed';
    } else if (
      remediation?.kind === 'cleaner_data' &&
      remediation.owner === 'data'
    ) {
      condition = 'needs_cleaner_data';
    } else {
      condition = 'needs_cleaner_data';
      remediation = null;
    }
    diagnostic =
      'Dated prior value retained; comparison status requires confirmation.';
  }

  return finishCanonicalization(
    packet,
    eligibility,
    condition,
    remediation,
    diagnostic,
  );
}

function canonicalizeNoValue(
  packet: AskHelixDisplayValuePacketV1,
  reason: Extract<AskHelixDisplayValueStateV1, { kind: 'no_value' }>['reason'],
): CanonicalizedDisplayValuePacket {
  const eligibility: AskHelixDisplayValueEligibilityV1 = {
    standaloneVisible: false,
    currentOpportunitySetEligible: false,
    additiveCurrentTotalEligible: false,
    signedOffPackageEligible: false,
  };
  const changed =
    !eligibilityMatches(packet.eligibility, eligibility) ||
    packet.condition !== reason;
  return finishCanonicalization(
    packet,
    eligibility,
    reason,
    packet.remediation,
    changed ? 'No value is available; display metadata was conservatively corrected.' : null,
  );
}

function finishCanonicalization(
  packet: AskHelixDisplayValuePacketV1,
  eligibility: AskHelixDisplayValueEligibilityV1,
  condition: AskHelixExecutiveValueConditionV1,
  remediation: AskHelixValueRemediationV1 | null,
  diagnostic: string | null,
): CanonicalizedDisplayValuePacket {
  const changed =
    !eligibilityMatches(packet.eligibility, eligibility) ||
    packet.condition !== condition ||
    packet.remediation !== remediation;
  if (!changed) return { packet, diagnostics: [] };
  return {
    packet: { ...packet, eligibility, condition, remediation },
    diagnostics: [
      diagnostic ?? 'Current value retained; eligibility was conservatively corrected.',
    ],
  };
}

function conservativeCurrentFallback(
  remediation: AskHelixValueRemediationV1 | null,
): Pick<AskHelixDisplayValuePacketV1, 'condition' | 'remediation'> {
  if (remediation?.kind === 'resolve_overlap' && remediation.owner === 'finance') {
    return { condition: 'figures_may_overlap', remediation };
  }
  if (remediation?.kind === 'finance_signoff' && remediation.owner === 'finance') {
    return { condition: 'awaiting_finance_signoff', remediation };
  }
  if (remediation?.kind === 'cleaner_data' && remediation.owner === 'data') {
    return { condition: 'needs_cleaner_data', remediation };
  }
  if (remediation?.kind === 'refresh_calculation' && remediation.owner === 'engineering') {
    return { condition: 'refresh_needed', remediation };
  }
  return {
    condition: 'needs_cleaner_data',
    remediation: null,
  };
}

function eligibilityMatches(
  actual: AskHelixDisplayValueEligibilityV1,
  expected: AskHelixDisplayValueEligibilityV1,
): boolean {
  return ELIGIBILITY_KEYS.every((key) => actual[key] === expected[key]);
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isOneOf<T extends string>(value: unknown, allowed: readonly T[]): value is T {
  return typeof value === 'string' && allowed.includes(value as T);
}

function rejectUnexpectedKeys(
  value: Readonly<Record<string, unknown>>,
  allowedKeys: readonly string[],
  path: string,
  errors: string[],
): void {
  for (const key of Object.keys(value)) {
    if (!allowedKeys.includes(key)) {
      errors.push(`${path} contains unsupported field ${key}`);
    }
  }
}

function hasUnexpectedKeys(
  value: Readonly<Record<string, unknown>>,
  allowedKeys: readonly string[],
): boolean {
  return Object.keys(value).some((key) => !allowedKeys.includes(key));
}

function rejectDuplicateNumericAuthorityFields(
  value: unknown,
  path: string,
  errors: string[],
): void {
  const pending: Array<{
    value: unknown;
    path: string;
    depth: number;
    numericAuthorityPath: string | null;
  }> = [{ value, path, depth: 0, numericAuthorityPath: null }];
  const visited = new WeakSet<object>();
  const reportedAuthorityPaths = new Set<string>();
  let visitedNodes = 0;

  while (pending.length > 0) {
    const current = pending.pop();
    if (
      !current ||
      typeof current.value !== 'object' ||
      current.value === null ||
      visited.has(current.value)
    ) {
      continue;
    }
    if (
      current.depth > PRESENTATION_METADATA_DEPTH_LIMIT ||
      visitedNodes >= PRESENTATION_METADATA_NODE_LIMIT
    ) {
      errors.push('packet presentation metadata exceeds validation limit');
      return;
    }

    visited.add(current.value);
    visitedNodes += 1;
    const entries = Array.isArray(current.value)
      ? current.value.map((entry, index) => [String(index), entry] as const)
      : Object.entries(current.value);

    for (const [key, nestedValue] of entries) {
      const nestedPath = Array.isArray(current.value)
        ? `${current.path}[${key}]`
        : `${current.path}.${key}`;
      const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
      const numericAuthorityPath = DUPLICATE_NUMERIC_AUTHORITY_KEYS.has(
        normalizedKey,
      )
        ? nestedPath
        : current.numericAuthorityPath;

      if (
        numericAuthorityPath &&
        isNumericAuthorityScalar(nestedValue) &&
        !reportedAuthorityPaths.has(numericAuthorityPath)
      ) {
        errors.push(
          `packet contains duplicate numeric authority field ${numericAuthorityPath}`,
        );
        reportedAuthorityPaths.add(numericAuthorityPath);
      }

      if (typeof nestedValue === 'object' && nestedValue !== null) {
        pending.push({
          value: nestedValue,
          path: nestedPath,
          depth: current.depth + 1,
          numericAuthorityPath,
        });
      }
    }
  }
}

function isNumericAuthorityScalar(value: unknown): boolean {
  if (typeof value === 'number' || typeof value === 'bigint') {
    return true;
  }
  if (typeof value !== 'string') {
    return false;
  }
  const trimmedValue = value.trim();
  const normalizedValue =
    trimmedValue.startsWith('(') && trimmedValue.endsWith(')')
      ? trimmedValue.slice(1, -1).trim()
      : trimmedValue;
  return (
    normalizedValue.length > 0 &&
    NUMERIC_AUTHORITY_STRING_PATTERN.test(normalizedValue)
  );
}
